import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import { apiRequest } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const AdminUsers = () => {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await apiRequest('/api/auth/admin/pending-users');
      if (resp.data && resp.data.success) {
        setPending(resp.data.users || []);
      } else {
        setError((resp.data && resp.data.error) || 'Failed to load pending users');
      }
    } catch (e) {
      setError(e.message || 'Failed to load pending users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    setActing(id);
    setError('');
    setNotice('');
    try {
      const resp = await apiRequest(`/api/auth/admin/users/${id}/${action}`, { method: 'POST' });
      if (resp.data && resp.data.success) {
        setNotice(`User ${action}d.`);
        setPending(p => p.filter(u => u.id !== id));
      } else {
        setError((resp.data && resp.data.error) || `Failed to ${action} user`);
      }
    } catch (e) {
      setError(e.message || `Failed to ${action} user`);
    } finally {
      setActing(null);
    }
  };

  if (!user?.isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Admin access required.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        New signups appear here after verifying their email. Approving activates the account and emails the user.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : pending.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No pending signups.</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ background: 'transparent' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email Verified</TableCell>
                    <TableCell>Signed Up</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pending.map(u => (
                    <TableRow key={u.id} hover>
                      <TableCell><b>{u.username}</b></TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={u.emailVerified ? 'Verified' : 'Unverified'}
                          color={u.emailVerified ? 'success' : 'default'} variant="outlined" />
                      </TableCell>
                      <TableCell>{u.createdAt ? new Date(u.createdAt).toLocaleString() : '—'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="contained" color="success" sx={{ mr: 1 }}
                          disabled={acting === u.id} onClick={() => act(u.id, 'approve')}>
                          Approve
                        </Button>
                        <Button size="small" variant="outlined" color="error"
                          disabled={acting === u.id} onClick={() => act(u.id, 'reject')}>
                          Reject
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminUsers;
